/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect, useRef } from 'react';
import { useSentryProvider } from '@ldp/error-boundary';
import { useSettingsContext } from '@ldp/common-contexts';
import { useViewResolverContext } from '@ldp/view-resolver-lite';
import { AD_EXCEPTIONS, TMediaAdExceptionContext } from '@ldp/media-analytics';

/**
 *
 * @param fetchOptions request parameters options
 * @param endpoint the media end points it fetches from
 * @param enabled if request should be sent
 * @param setSnowplowException function expecting TMediaExceptionContext as args
 * @returns Object {
 *  data: next gen sponsored display data. It contains one or more media ads
 *  error: the error obj from the api call
 *  isFetching: flag to indicate the fetching state
 * }
 */
export function useFetchAds({
  fetchOptions,
  envConfig,
  ciamAccessToken,
  endpoint = 'media/ads',
  cacheBuster = null,
  enabled = true,
  reservedId = '',
  setExceptionContext,
  traceId,
}: {
  fetchOptions: RequestInit;
  envConfig?: {
    sduiBaseUrl: string;
    apiKey: string;
  };
  ciamAccessToken?: string;
  endpoint?: string;
  cacheBuster?: string | number | null;
  enabled?: boolean;
  reservedId?: string;
  setExceptionContext?: (exceptionContext: TMediaAdExceptionContext) => void;
  traceId: string;
}) {
  const { captureException } = useSentryProvider();
  const viewResolver = useViewResolverContext();
  const settingsContext = useSettingsContext();
  const config = envConfig || viewResolver;
  const bffBaseUrl =
    settingsContext?.mediaBaseUrl ||
    settingsContext?.mediaConfigBaseUrl ||
    (config as any)?.sduiBffUrl ||
    config?.sduiBaseUrl?.replace('/sdui/views', '');
  const apiKey = config?.apiKey;
  const lang = viewResolver?.locale;

  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);

  // Use ref to track if component is mounted
  const isMountedRef = useRef(true);

  // Use ref to store the previous request key to avoid unnecessary refetches
  const previousRequestKeyRef = useRef<string>('');

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // Create a unique key for this request to detect changes
    const requestKey = JSON.stringify([fetchOptions?.body, cacheBuster]);

    // Skip if not enabled or if request hasn't changed
    if (!enabled || requestKey === previousRequestKeyRef.current) {
      // If fetch is disabled, ensure isFetching is false
      if (!enabled && isFetching) {
        setIsFetching(false);
      }
      return;
    }

    previousRequestKeyRef.current = requestKey;

    const fetchNextGenAd = async () => {
      setIsFetching(true);
      setError(null);

      // convert header to lower case for case-insensitve override
      const headers = Object.entries(fetchOptions?.headers || {}).reduce(
        (lowerCaseHeaders, [headerKey, headerValue]) => ({
          ...lowerCaseHeaders,
          [headerKey.toLowerCase()]: headerValue,
        }),
        {}
      );
      const options: RequestInit = {
        ...fetchOptions,
        method: 'POST',
        headers: {
          ...headers,
          // override headers
          'x-apikey': apiKey,
          'x-application-type': 'Web',
          'content-type': 'application/json',
          'X-Correlation-ID': traceId,
          // optional override headers
          ...(lang && { 'accept-language': lang }),
          ...(ciamAccessToken && {
            authorization: `Bearer ${ciamAccessToken}`,
          }),
        },
      };

      try {
        if (!bffBaseUrl) {
          throw new Error('BFF base URL is not configured');
        }

        const url = `${bffBaseUrl}/${endpoint}`;

        const resp = await fetch(url, options);
        const responseData = await resp.json();

        // Only update state if component is still mounted
        if (!isMountedRef.current) {
          return;
        }

        if (!resp.ok) {
          setExceptionContext?.({
            type: AD_EXCEPTIONS.AD_FAILED,
            code: resp.status + '',
            message: resp.statusText,
            process: reservedId,
          });
          // Don't set data on error responses
          setIsFetching(false);
          return;
        }

        if (responseData.components?.length === 0) {
          setExceptionContext?.({
            type: AD_EXCEPTIONS.AD_NULL,
            code: '200',
            message: 'No ad available',
            process: reservedId,
          });
        }

        setData({ data: responseData });
        setIsFetching(false);
      } catch (err) {
        if (!isMountedRef.current) {
          return;
        }

        const error = new Error(err instanceof Error ? err.message : String(err));
        setError(error);
        setIsFetching(false);

        setExceptionContext?.({
          type: AD_EXCEPTIONS.AD_FAILED,
          message: (error + '').substring(0, 28),
          process: reservedId,
        });

        captureException(error, {
          tags: {
            section: 'MediaComponents',
          },
        });
      }
    };

    fetchNextGenAd();
  }, [
    fetchOptions?.body,
    cacheBuster,
    enabled,
    bffBaseUrl,
    endpoint,
    apiKey,
    lang,
    ciamAccessToken,
    traceId,
    reservedId,
    setExceptionContext,
    captureException,
  ]);

  const returnData = {
    error: data?.errors || error,
    data: data?.data,
    isFetching,
  };

  return returnData;
}
